import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { Alert, Button, Form, FormGroup, Input, Label } from 'reactstrap';
import axios from 'axios';
import { API_BASE_CANDIDATES, SITE_BASE_CANDIDATES } from '../../apiConfig';
import { clearStudentPortalContext } from './studentPortalStorage';
import StudentThemeToggle from './StudentThemeToggle';

const parseLegacyFanOptions = options =>
  (options || [])
    .map(optionMarkup => {
      if (typeof optionMarkup !== 'string') {
        return null;
      }

      const valueMatch = optionMarkup.match(/value=['"]([^'"]+)['"]/i);
      const label = optionMarkup.replace(/<[^>]*>/g, '').trim();

      if (!valueMatch || !label) {
        return null;
      }

      return {
        value: valueMatch[1],
        label
      };
    })
    .filter(Boolean);

const StudentPortalForm = ({ onSwitchToAdmin }) => {
  const [passport, setPassport] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [studentImage, setStudentImage] = useState('');

  const siteBase = SITE_BASE_CANDIDATES[0] || 'https://psixologiya.suvoriy.com/';
  const defaultImage = `${siteBase}/rasm/talaba/logo.png`;

  const endpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/test/portal`),
    []
  );

  const activeImage = studentImage || defaultImage;
  const submitDisabled =
    isSubmitting || !passport.trim() || (isLocked && !selectedSubject);

  useEffect(() => {
    clearStudentPortalContext();
  }, []);

  const resetStudentFlow = () => {
    setSelectedSubject('');
    setSubjects([]);
    setIsLocked(false);
    setStudentImage('');
  };

  const resetToInitialState = () => {
    setPassport('');
    resetStudentFlow();
  };

  const resolveStudentImage = imagePath => {
    if (!imagePath || typeof imagePath !== 'string') {
      return defaultImage;
    }

    if (/^https?:\/\//i.test(imagePath)) {
      return imagePath;
    }

    return `${siteBase}/rasm/talaba/${imagePath.replace(/^\/+/, '')}`;
  };

  const submitStudentRequest = async payload => {
    let lastError = null;

    for (const endpoint of endpointCandidates) {
      try {
        const body = new URLSearchParams();
        body.set('pasport', payload.passport);
        body.set('fan', payload.subject);

        return await axios.post(endpoint, body, {
          timeout: 15000,
          withCredentials: true
        });
      } catch (error) {
        lastError = error;
        const status = error.response?.status || 0;
        if (status && ![404, 502, 503, 504].includes(status)) {
          throw error;
        }
      }
    }

    throw lastError;
  };

  const openStudentPassport = () => {
    window.location.assign('/authentication/basic/student-passport');
  };

  const openStudentTest = () => {
    window.location.assign('/authentication/basic/student-test');
  };

  const handleFirstStepSuccess = data => {
    const structuredOptions = Array.isArray(data.fan_options) && data.fan_options.length
      ? data.fan_options
      : parseLegacyFanOptions(data.fan);

    setErrorMessage('');
    setSubjects(structuredOptions);
    setSelectedSubject('');
    setIsLocked(true);
    setStudentImage(resolveStudentImage(data.rasm));
  };

  const handleFailureReset = message => {
    setErrorMessage(message || '');
    resetStudentFlow();
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      const response = await submitStudentRequest({
        passport: passport.trim(),
        subject: isLocked ? selectedSubject : 'undefined'
      });

      const data = response.data || {};

      switch (data.malumot) {
        case 0:
          handleFirstStepSuccess(data);
          break;
        case 1:
          handleFailureReset('Test fani yuklanmagan');
          toast.info('Test fani yuklanmagan');
          break;
        case 2:
          handleFailureReset('Noto`g`ri Hemis ID kiritilgan');
          toast.error('Noto`g`ri Hemis ID kiritilgan');
          break;
        case 3:
          handleFailureReset('Hemis ID maydoni to`ldirilmagan');
          toast.info('Iltimos Hemis ID maydonini to`ldiring');
          break;
        case 4:
          setErrorMessage('Fanni tanlash kerak');
          toast.info('Iltimos fanni tanlang');
          break;
        case 5:
          toast.info('Siz bu fandan test yechgansiz');
          resetToInitialState();
          break;
        case 6:
          toast.error('Siz testdan chetlashtirilgansiz');
          resetToInitialState();
          break;
        case 7:
          openStudentTest();
          return;
        case 8:
          toast.error('Siz testga kirish uchun yetarli ball to`plamagansiz');
          resetToInitialState();
          break;
        case 9:
          openStudentPassport();
          return;
        case 10:
          setErrorMessage('Baza bilan ulanishda muammo bor');
          toast.error('Baza bilan ulanishda muammo bor');
          break;
        case 11:
          handleFailureReset(data.message || 'Sizga joriy davr uchun test biriktirilmagan');
          toast.info(data.message || 'Sizga joriy davr uchun test biriktirilmagan');
          break;
        default:
          setErrorMessage('Kirishda noma`lum xatolik yuz berdi');
          break;
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        (typeof error.response?.data === 'string' ? error.response.data : '') ||
        'Talaba portali bilan ulanishda xato yuz berdi';

      setErrorMessage(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="psi-student-portal__toolbar">
        <StudentThemeToggle />
      </div>

      <div className="psi-login-panel__top psi-login-panel__top--student">
        <div className="psi-login-panel__mark psi-login-panel__mark--student">
          <img
            src={activeImage}
            alt="Talaba rasmi"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = defaultImage;
            }}
          />
        </div>
        <div className="psi-login-panel__headline">
          <h2 className="psi-login-title">Talaba kirishi</h2>
        </div>
      </div>

      <div className="psi-login-panel__divider" />

      <Form className="psi-login-form psi-login-form--student" onSubmit={handleSubmit}>
        {errorMessage ? (
          <Alert color="danger" className="psi-login-alert">
            {errorMessage}
          </Alert>
        ) : null}

        <FormGroup className="mb-3">
          <Label className="psi-login-label">Hemis ID</Label>
          <div className="psi-login-field">
            <div className="psi-login-field__icon">
              <FontAwesomeIcon icon="id-card" />
            </div>
            <Input
              className="psi-login-input psi-login-input--framed"
              placeholder="Hemis ID ni kiriting"
              value={passport}
              onChange={({ target }) => setPassport(target.value)}
              autoComplete="off"
              type="text"
              readOnly={isLocked}
            />
          </div>
        </FormGroup>

        {isLocked ? (
          <FormGroup className="mb-3">
            <Label className="psi-login-label">Fan</Label>
            <div className="psi-login-field">
              <div className="psi-login-field__icon">
                <FontAwesomeIcon icon="book" />
              </div>
              <Input
                className="psi-login-input psi-login-input--framed"
                type="select"
                value={selectedSubject}
                onChange={({ target }) => setSelectedSubject(target.value)}
              >
                <option value="">Fanni tanlang</option>
                {subjects.map(subject => (
                  <option key={subject.value} value={subject.value}>
                    {subject.label}
                  </option>
                ))}
              </Input>
            </div>
          </FormGroup>
        ) : null}

        <FormGroup className="mb-0">
          <Button color="primary" block className="psi-login-submit" disabled={submitDisabled}>
            {isSubmitting ? 'Tekshirilmoqda...' : isLocked ? 'Testni boshlash' : 'Kirish'}
          </Button>
        </FormGroup>
      </Form>

      <div className="psi-auth-switch">
        <button type="button" className="psi-auth-switch__link" onClick={onSwitchToAdmin}>
          Login va parol bilan kirish
        </button>
      </div>
    </>
  );
};

StudentPortalForm.propTypes = {
  onSwitchToAdmin: PropTypes.func.isRequired
};

export default StudentPortalForm;
